// The decision on one candidate — confirm or merge, and reject — shared by the
// star's details panel, the candidate table and the document overlay's faces
// rail, so every place offers the same buttons (ADR-0039, ADR-0042).
import { GitMerge } from 'lucide-react'
import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'

const PM = 'cases.investigate.entityResolution.photoMatching'

/** What the analyst can do with a candidate of the star or the table, and the face in flight. */
export interface CandidateActions {
  /** Confirm the pair face–identity. */
  onConfirm: (faceId: string) => void
  /** Reject it: ADR-0039 never suggests a rejected pair again. */
  onReject: (faceId: string) => void
  /**
   * Merge this identity with the one the face is already confirmed to
   * (ADR-0042). The caller asks first, with `MergeIdentitiesDialog`.
   */
  onMerge: (faceId: string) => void
  /** The face whose decision or merge is in flight, so its buttons stop accepting clicks. */
  pendingFaceId?: string | null
}

/** One candidate's handlers, its face and identity already bound by the caller. */
export interface DecisionHandlers {
  confirm: () => void
  reject: () => void
  merge: () => void
}

/** The buttons' words, when a surface keeps its own copy. */
export interface DecisionLabels {
  confirm: string
  reject: string
  merge: string
}

/** The star's and the table's actions, bound to one face. */
export function bindCandidateActions(actions: CandidateActions, faceId: string): DecisionHandlers {
  return {
    confirm: () => actions.onConfirm(faceId),
    reject: () => actions.onReject(faceId),
    merge: () => actions.onMerge(faceId),
  }
}

/**
 * Confirm and reject for a face nobody owns yet; merge and reject for a face
 * already confirmed to another identity — a face belongs to one identity, so
 * confirming it there could only be refused (ADR-0042). Every click stops at its
 * button, so a clickable row around them does not also open or select the face.
 */
export function CandidateDecision({
  assigned,
  disabled,
  on,
  labels,
  className,
}: {
  /** The face is already confirmed to another identity. */
  assigned: boolean
  /** A decision or a merge is in flight for this face. */
  disabled: boolean
  on: DecisionHandlers
  /** The photo-matching words unless the surface keeps its own. */
  labels?: DecisionLabels
  className?: string
}) {
  const { t } = useTranslation()
  const words = labels ?? {
    confirm: t(`${PM}.actions.confirm`),
    reject: t(`${PM}.actions.reject`),
    merge: t(`${PM}.actions.merge`),
  }
  const run = (handler: () => void) => (event: MouseEvent) => {
    event.stopPropagation()
    handler()
  }

  return (
    <span className={cn('flex gap-1', className)}>
      {assigned ? (
        <Button
          type="button"
          size="sm"
          disabled={disabled}
          data-decision="merge"
          onClick={run(on.merge)}
        >
          <GitMerge aria-hidden />
          {words.merge}
        </Button>
      ) : (
        <Button
          type="button"
          size="sm"
          disabled={disabled}
          data-decision="confirm"
          onClick={run(on.confirm)}
        >
          {words.confirm}
        </Button>
      )}
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled}
        data-decision="reject"
        onClick={run(on.reject)}
      >
        {words.reject}
      </Button>
    </span>
  )
}
